import {
	CurrencyCode,
	CurrencyDropdown,
	CurrencyDropdownItem,
	CurrencyName,
	EmptyCurrencyDropdownItem,
} from '@components/CurrencySelect/CurrencySelect.styles'
import { Currency, DEFAULT_CURRENCY, filterCurrencies, getCurrencyByCode } from '@/constants/currencies'
import { usePortalDropdownPosition } from '@hooks'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { PickerSearch, TileButton, TileCode, TileWrap } from './CurrencyTile.styles'

/** The picker list is wider than its 72px tile anchor. */
const PANEL_WIDTH = 320

interface CurrencyTileProps {
	/** Lowercase ISO 4217 code. */
	value: string
	onChange: (currencyCode: string) => void
}

/**
 * The selected currency as a tile — symbol on an orange square, code beneath,
 * like the mobile flow. Tapping it opens the same searchable currency list
 * the form selector uses, portaled under the tile.
 */
const CurrencyTile: React.FC<CurrencyTileProps> = ({ value, onChange }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [query, setQuery] = useState('')
	const anchorRef = useRef<HTMLDivElement>(null)
	const panelRef = useRef<HTMLDivElement>(null)
	const position = usePortalDropdownPosition(anchorRef, isOpen)

	const selected = getCurrencyByCode(value) ?? DEFAULT_CURRENCY
	const code = selected.code.toUpperCase()
	const matches = filterCurrencies(query)

	const close = () => {
		setIsOpen(false)
		setQuery('')
	}

	useEffect(() => {
		if (!isOpen) return
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node
			if (!anchorRef.current?.contains(target) && !panelRef.current?.contains(target)) close()
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen])

	const pick = (currency: Currency) => {
		onChange(currency.code)
		close()
	}

	const handlePanelKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') close()
	}

	return (
		<TileWrap ref={anchorRef}>
			<TileButton
				type="button"
				aria-label={`Default currency: ${code}`}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				onClick={() => (isOpen ? close() : setIsOpen(true))}
			>
				{selected.symbol}
			</TileButton>
			<TileCode aria-hidden>{code}</TileCode>
			{isOpen &&
				position &&
				createPortal(
					<CurrencyDropdown
						ref={panelRef}
						role="listbox"
						$top={position.top}
						$left={position.left}
						$width={PANEL_WIDTH}
						onKeyDown={handlePanelKeyDown}
					>
						<PickerSearch
							aria-label="Search currency"
							placeholder="Search currency..."
							value={query}
							onChange={e => setQuery(e.target.value)}
							autoFocus
						/>
						{matches.length > 0 ? (
							matches.map(currency => (
								<CurrencyDropdownItem
									key={currency.code}
									role="option"
									aria-selected={currency.code === value}
									$isSelected={currency.code === value}
									onClick={() => pick(currency)}
								>
									<CurrencyCode>
										{currency.symbol} {currency.code.toUpperCase()}
									</CurrencyCode>
									<CurrencyName>{currency.name}</CurrencyName>
								</CurrencyDropdownItem>
							))
						) : (
							<EmptyCurrencyDropdownItem $isSelected={false}>No currencies found</EmptyCurrencyDropdownItem>
						)}
					</CurrencyDropdown>,
					document.body,
				)}
		</TileWrap>
	)
}

export default CurrencyTile
